import { useEffect, useMemo, useRef, useState } from 'react';

import type { NativeBooking, NativeBookingApi, NativeResultPdf } from './native-booking-api';
import {
  ariaSortValue,
  DEFAULT_MY_BOOKINGS_SORT,
  nextMyBookingsSort,
  sortMyBookings,
  type MyBookingsSortKey,
  type MyBookingsSortState,
} from './native-booking-my-sort';
import {
  formatEkbDateTime,
  nativeBookingStatusLabel,
  nativeBookingStatusTone,
} from './native-booking-presentation';
import { nativeProblemMessage } from './native-booking-problem';
import { navigate } from './path-router';

type StatusFilter = 'all' | 'CONFIRMED' | 'STARTED' | 'CANCELLED' | 'COMPLETED';

const SORT_HEADERS: readonly {
  readonly key: MyBookingsSortKey;
  readonly label: string;
  readonly testId: string;
}[] = [
  { key: 'public_number', label: '№ заявки', testId: 'native-mine-sort-public-number' },
  { key: 'slot_start_at', label: 'Дата и время (ЕКБ)', testId: 'native-mine-sort-slot' },
  { key: 'site', label: 'Площадка', testId: 'native-mine-sort-site' },
  { key: 'equipment', label: 'Вид техники', testId: 'native-mine-sort-equipment' },
  { key: 'status', label: 'Состояние', testId: 'native-mine-sort-status' },
];

export function triggerBrowserDownload(blob: Blob, filename: string): () => void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  return () => {
    URL.revokeObjectURL(objectUrl);
  };
}

function resultPdfOf(row: NativeBooking): NativeResultPdf | undefined {
  return row.result_pdf;
}

export function NativeBookingMyPage(props: {
  readonly api: NativeBookingApi;
  readonly catalogSites: readonly { id: string; display_name: string }[];
  readonly catalogEquipment: readonly { id: string; display_name: string }[];
}): React.ReactElement {
  const [rows, setRows] = useState<readonly NativeBooking[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<MyBookingsSortState>(DEFAULT_MY_BOOKINGS_SORT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<NativeBooking | null>(null);
  const [cancelInFlight, setCancelInFlight] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const cancelInFlightRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const revokeObjectUrlRef = useRef<(() => void) | null>(null);

  const siteNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const site of props.catalogSites) {
      map.set(site.id, site.display_name);
    }
    return map;
  }, [props.catalogSites]);

  const equipmentNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const eq of props.catalogEquipment) {
      map.set(eq.id, eq.display_name);
    }
    return map;
  }, [props.catalogEquipment]);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      setRows(await props.api.listBookings());
    } catch (failure) {
      setError(nativeProblemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [props.api]);

  useEffect(() => {
    return () => {
      revokeObjectUrlRef.current?.();
      revokeObjectUrlRef.current = null;
    };
  }, []);

  const closeCancelDialog = () => {
    setCancelTarget(null);
    const prior = previouslyFocused.current;
    previouslyFocused.current = null;
    if (prior !== null) {
      prior.focus();
    }
  };

  useEffect(() => {
    if (cancelTarget === null) {
      return;
    }
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = dialogRef.current;
    const focusable = dialog?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusable?.[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeCancelDialog();
        return;
      }
      if (event.key !== 'Tab' || dialog === null || focusable === undefined || focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [cancelTarget]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (filter === 'CONFIRMED') {
        if (row.status !== 'CONFIRMED' || row.case_status === 'IN_PROGRESS') {
          return false;
        }
      } else if (filter === 'STARTED') {
        if (row.case_status !== 'IN_PROGRESS') {
          return false;
        }
      } else if (filter === 'CANCELLED') {
        if (row.status !== 'CANCELLED') {
          return false;
        }
      } else if (filter === 'COMPLETED') {
        if (row.case_status !== 'COMPLETED' && row.case_status !== 'APPROVED' && row.case_status !== 'REJECTED') {
          return false;
        }
      }
      if (q === '') {
        return true;
      }
      const hay = [
        row.public_number,
        siteNames.get(row.inspection_site_id) ?? row.inspection_site_id,
        equipmentNames.get(row.equipment_type_id) ?? row.equipment_type_id,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [equipmentNames, filter, rows, search, siteNames]);

  const visibleRows = useMemo(() => {
    const siteName = (row: NativeBooking) =>
      siteNames.get(row.inspection_site_id) ?? row.inspection_site_id;
    const equipmentName = (row: NativeBooking) =>
      equipmentNames.get(row.equipment_type_id) ?? row.equipment_type_id;
    return sortMyBookings(filtered, sort, siteName, equipmentName);
  }, [equipmentNames, filtered, siteNames, sort]);

  const openBooking = (id: string) => {
    navigate(`/booking?booking=${encodeURIComponent(id)}`);
  };

  const confirmCancelBooking = async () => {
    const row = cancelTarget;
    if (row === null || row.current_inspection_case_id === null) {
      return;
    }
    if (cancelInFlightRef.current) {
      return;
    }
    cancelInFlightRef.current = true;
    setCancelInFlight(true);
    setError(null);
    try {
      await props.api.cancel(row.id, {
        expected_version: row.version,
        expected_case_id: row.current_inspection_case_id,
        idempotencyKey: crypto.randomUUID(),
      });
      setCancelTarget(null);
      await reload();
    } catch (failure) {
      setError(nativeProblemMessage(failure));
      // Keep original rows; dialog stays open so the user can retry or leave.
    } finally {
      cancelInFlightRef.current = false;
      setCancelInFlight(false);
    }
  };

  const startEdit = async (row: NativeBooking) => {
    if (row.current_inspection_case_id === null) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await props.api.openEditProposal(row.id, {
        expected_version: row.version,
        expected_case_id: row.current_inspection_case_id,
        idempotencyKey: crypto.randomUUID(),
      });
      navigate(
        `/booking?booking=${encodeURIComponent(row.id)}&proposal=${encodeURIComponent(result.edit_proposal.id)}`,
      );
    } catch (failure) {
      setError(nativeProblemMessage(failure));
    } finally {
      setBusy(false);
    }
  };

  const downloadSignedPdf = async (row: NativeBooking) => {
    if (resultPdfOf(row)?.availability !== 'AVAILABLE') {
      return;
    }
    setPdfBusyId(row.id);
    setError(null);
    try {
      const downloaded = await props.api.downloadSignedPdf(row.id);
      revokeObjectUrlRef.current?.();
      revokeObjectUrlRef.current = triggerBrowserDownload(downloaded.blob, downloaded.filename);
    } catch (failure) {
      setError(nativeProblemMessage(failure));
    } finally {
      setPdfBusyId(null);
    }
  };

  const cancelSiteName =
    cancelTarget === null
      ? ''
      : (siteNames.get(cancelTarget.inspection_site_id) ?? cancelTarget.inspection_site_id);

  return (
    <div className="native-mine-page">
      <div className="native-page-head native-mine-head">
        <div>
          <h1>Мои заявки</h1>
          <p>Все отправленные заявки. Изменить или отменить можно до начала осмотра.</p>
        </div>
        <button type="button" className="native-btn native-btn-primary" onClick={() => navigate('/booking')}>
          + Новая заявка
        </button>
      </div>
      {error !== null ? (
        <p role="alert" className="native-banner native-banner-danger">
          {error}
        </p>
      ) : null}
      <section className="native-panel native-mine-panel">
        <div className="native-mine-toolbar">
          <label className="native-field">
            Поиск
            <input
              className="native-input"
              value={search}
              placeholder="Номер, площадка или техника"
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </label>
          <label className="native-field">
            Статус
            <select
              className="native-input"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value as StatusFilter);
              }}
            >
              <option value="all">Все заявки</option>
              <option value="CONFIRMED">Ожидают осмотра</option>
              <option value="STARTED">Осмотр начат</option>
              <option value="CANCELLED">Отменены</option>
              <option value="COMPLETED">Завершены</option>
            </select>
          </label>
        </div>
        <p className="native-records-count">
          {busy ? 'Загрузка…' : `Показано: ${visibleRows.length} из ${rows.length}`}
        </p>
        <div className="native-table-scroll">
          <table className="native-records-table">
            <thead>
              <tr>
                {SORT_HEADERS.map((header) => {
                  const aria = ariaSortValue(sort, header.key);
                  const active = sort.key === header.key;
                  return (
                    <th key={header.key} aria-sort={aria} scope="col">
                      <button
                        type="button"
                        className={
                          active
                            ? 'native-mine-sort-btn native-mine-sort-btn-active'
                            : 'native-mine-sort-btn'
                        }
                        data-testid={header.testId}
                        data-sort-key={header.key}
                        data-sort-dir={active ? sort.dir : undefined}
                        aria-label={
                          active
                            ? `${header.label}, сортировка ${sort.dir === 'asc' ? 'по возрастанию' : 'по убыванию'}`
                            : `Сортировать по колонке ${header.label}`
                        }
                        onClick={() => {
                          setSort((current) => nextMyBookingsSort(current, header.key));
                        }}
                      >
                        <span>{header.label}</span>
                        {active ? (
                          <span className="native-mine-sort-indicator" aria-hidden="true">
                            {sort.dir === 'asc' ? '▲' : '▼'}
                          </span>
                        ) : null}
                      </button>
                    </th>
                  );
                })}
                <th scope="col">Действия</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="native-mine-empty">
                    {busy ? 'Загрузка…' : 'Заявок пока нет.'}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => {
                  const tone = nativeBookingStatusTone(row);
                  const resultPdf = resultPdfOf(row);
                  const pdfAvailable = resultPdf?.availability === 'AVAILABLE';
                  return (
                    <tr key={row.id} data-testid={`native-mine-row-${row.id}`}>
                      <td>{row.public_number}</td>
                      <td data-slot-start-at={row.slot_start_at}>{formatEkbDateTime(row.slot_start_at)}</td>
                      <td>{siteNames.get(row.inspection_site_id) ?? row.inspection_site_id}</td>
                      <td>{equipmentNames.get(row.equipment_type_id) ?? row.equipment_type_id}</td>
                      <td>
                        <span className={`native-pill native-pill-${tone}`}>
                          {nativeBookingStatusLabel(row)}
                        </span>
                        {resultPdf !== undefined && resultPdf.availability !== 'AVAILABLE' ? (
                          <p
                            className="native-pdf-status"
                            data-testid={`native-pdf-status-${row.id}`}
                            data-availability={resultPdf.availability}
                          >
                            {resultPdf.message}
                          </p>
                        ) : null}
                      </td>
                      <td className="native-action-cell">
                        <button
                          type="button"
                          className="native-btn native-btn-small native-btn-secondary"
                          onClick={() => {
                            openBooking(row.id);
                          }}
                        >
                          Открыть
                        </button>
                        {pdfAvailable ? (
                          <button
                            type="button"
                            className="native-btn native-btn-small native-btn-primary"
                            data-testid={`native-download-signed-pdf-${row.id}`}
                            disabled={busy || cancelInFlight || pdfBusyId === row.id}
                            onClick={() => {
                              void downloadSignedPdf(row);
                            }}
                          >
                            {pdfBusyId === row.id ? 'Скачивание…' : 'Скачать итоговый PDF'}
                          </button>
                        ) : null}
                        {row.editable ? (
                          <>
                            <button
                              type="button"
                              className="native-btn native-btn-small native-btn-secondary"
                              disabled={busy || cancelInFlight}
                              onClick={() => {
                                void startEdit(row);
                              }}
                            >
                              Изменить
                            </button>
                            <button
                              type="button"
                              className="native-btn native-btn-small native-btn-danger"
                              disabled={busy || cancelInFlight}
                              onClick={() => {
                                setCancelTarget(row);
                              }}
                            >
                              Отменить
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {cancelTarget !== null ? (
        <div
          className="native-modal-wrap"
          role="presentation"
          data-testid="native-cancel-dialog"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !cancelInFlight) {
              closeCancelDialog();
            }
          }}
        >
          <div
            ref={dialogRef}
            className="native-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="native-cancel-title"
          >
            <h2 id="native-cancel-title">Отменить отправленную заявку?</h2>
            <p>
              Будут отменены запись <b>{cancelTarget.public_number}</b> и ещё не начатый осмотр. Запланированный
              слот освободится; запись останется в истории заявок.
            </p>
            <div className="native-modal-stat">
              <span>Дата и время (ЕКБ)</span>
              <b>{formatEkbDateTime(cancelTarget.slot_start_at)}</b>
              <span>Площадка</span>
              <b>{cancelSiteName}</b>
            </div>
            <div className="native-modal-actions">
              <button
                type="button"
                className="native-btn native-btn-secondary"
                disabled={cancelInFlight}
                onClick={() => {
                  closeCancelDialog();
                }}
              >
                Оставить заявку
              </button>
              <button
                type="button"
                className="native-btn native-btn-danger"
                disabled={cancelInFlight}
                onClick={() => {
                  void confirmCancelBooking();
                }}
              >
                {cancelInFlight ? 'Отмена…' : 'Отменить заявку'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
